import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Receipt, CheckCircle2, XCircle, Loader2, RefreshCw, ExternalLink, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "pending_verification", label: "Awaiting verification" },
  { key: "pending_payment", label: "Awaiting payment" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_COLOR = {
  pending_payment: "text-amber-300 border-amber-500/40",
  pending_verification: "text-sky-300 border-sky-500/40",
  approved: "text-emerald-300 border-emerald-500/40",
  rejected: "text-rose-300 border-rose-500/40",
  expired: "text-zinc-400 border-zinc-500/40",
};

function ApproveRejectPanel({ intent, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  if (intent.status === "approved" || intent.status === "rejected") return null;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/manual/${intent.id}/approve`, { note: "Verified" });
      toast.success("Approved");
      onChange && onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Approve failed"));
    } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/payments/manual/${intent.id}/reject`, { reason });
      toast.success("Rejected");
      onChange && onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Reject failed"));
    } finally { setBusy(false); setShowReject(false); }
  };

  return (
    <div className="flex items-center gap-2 mt-3">
      <button
        data-testid={`admin-pay-approve-${intent.id}`}
        onClick={approve} disabled={busy}
        className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/40 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
      </button>
      {!showReject ? (
        <button
          data-testid={`admin-pay-reject-${intent.id}`}
          onClick={() => setShowReject(true)}
          className="inline-flex items-center gap-1 text-xs text-rose-300 hover:bg-rose-500/10 border border-rose-500/40 rounded-md px-3 py-1.5 transition-colors"
        >
          <XCircle className="w-3 h-3" /> Reject
        </button>
      ) : (
        <div className="flex items-center gap-2 flex-1">
          <input
            data-testid={`admin-pay-reject-reason-${intent.id}`}
            type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason..."
            className="flex-1 bg-black/40 border border-rose-500/40 rounded-md px-2 py-1 text-xs text-white"
          />
          <button onClick={reject} disabled={busy} className="text-xs text-rose-300 border border-rose-500/40 rounded-md px-2 py-1 hover:bg-rose-500/10 disabled:opacity-50">Confirm</button>
          <button onClick={() => { setShowReject(false); setReason(""); }} className="text-xs text-white/50 hover:text-white">Cancel</button>
        </div>
      )}
    </div>
  );
}

export default function AdminManualPaymentsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_verification");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/admin/payments/manual", { params });
      setItems(r.data || []);
    } catch (err) {
      toast.error(errorMessage(err, "Load failed"));
    } finally { setLoading(false); }
  };
  const loadSettings = async () => {
    try {
      const r = await api.get("/admin/payments/manual/settings");
      setSettings(r.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { loadSettings(); }, []);

  const saveSettings = async () => {
    try {
      const r = await api.patch("/admin/payments/manual/settings", draft);
      setSettings(r.data);
      setEditing(false);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    }
  };

  return (
    <div data-testid="admin-manual-payments-tab" className="space-y-6">
      {/* Settings card */}
      <div className="rounded-md border border-gold/20 bg-black/30 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-gold font-display text-lg">Manual Payment Settings</div>
            <div className="text-xs text-white/60">Bank, Lynk JA, cash, crypto wallet addresses shown to users.</div>
          </div>
          {!editing ? (
            <button
              data-testid="admin-pay-edit-settings"
              onClick={() => { setDraft(settings || {}); setEditing(true); }}
              className="text-xs text-gold border border-gold/40 hover:bg-gold/10 rounded-md px-3 py-1.5"
            >Edit</button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(false)} className="text-xs text-white/50 hover:text-white">Cancel</button>
              <button onClick={saveSettings} className="text-xs text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-md px-3 py-1.5">Save</button>
            </div>
          )}
        </div>
        {settings && !editing && (
          <div className="grid sm:grid-cols-2 gap-3 text-xs text-white/70">
            <div><span className="text-gold/70">Lynk handle:</span> {settings.lynk_ja_handle || "—"}</div>
            <div><span className="text-gold/70">Lynk phone:</span> {settings.lynk_ja_phone || "—"}</div>
            <div className="sm:col-span-2"><span className="text-gold/70">Bank:</span> <pre className="whitespace-pre-line text-xs text-white/60 mt-1">{settings.bank_transfer_instructions || "—"}</pre></div>
            <div className="sm:col-span-2"><span className="text-gold/70">Wire:</span> <pre className="whitespace-pre-line text-xs text-white/60 mt-1">{settings.wire_instructions || "—"}</pre></div>
            <div className="sm:col-span-2">
              <span className="text-gold/70">Crypto wallets:</span>
              <ul className="mt-1 space-y-1">
                {(settings.crypto_wallets || []).map((w, i) => (
                  <li key={i} className="font-mono text-xs">{w.symbol} ({w.network}): <span className="text-gold/80">{w.address}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {editing && draft && (
          <div className="space-y-3 text-xs">
            <textarea
              data-testid="admin-pay-edit-bank"
              value={draft.bank_transfer_instructions || ""}
              onChange={(e) => setDraft({ ...draft, bank_transfer_instructions: e.target.value })}
              placeholder="Bank transfer instructions..."
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white" rows="4"
            />
            <textarea
              value={draft.wire_instructions || ""}
              onChange={(e) => setDraft({ ...draft, wire_instructions: e.target.value })}
              placeholder="Wire transfer instructions..."
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white" rows="4"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                value={draft.lynk_ja_handle || ""}
                onChange={(e) => setDraft({ ...draft, lynk_ja_handle: e.target.value })}
                placeholder="@lynk-handle"
                className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white"
              />
              <input
                value={draft.lynk_ja_phone || ""}
                onChange={(e) => setDraft({ ...draft, lynk_ja_phone: e.target.value })}
                placeholder="+1-876-…"
                className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white"
              />
            </div>
            <div className="text-xs text-white/50">Crypto wallets are edited via API (TODO: rich editor — stored as JSON).</div>
          </div>
        )}
      </div>

      {/* Queue */}
      <div className="rounded-md border border-white/10 bg-black/30 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gold/70" />
            {STATUSES.map((s) => (
              <button
                key={s.key} data-testid={`admin-pay-filter-${s.key}`}
                onClick={() => setFilter(s.key)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                  filter === s.key
                    ? "bg-gold/10 border-gold text-gold"
                    : "border-white/10 text-white/60 hover:border-gold/40 hover:text-gold"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="text-white/50 hover:text-gold p-1.5"><RefreshCw className="w-4 h-4" /></button>
        </div>

        {loading && <div className="text-center text-white/50 py-8">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-white/50 py-12">No payments in this state.</div>
        )}
        <div className="space-y-3">
          {items.map((p) => (
            <motion.div
              key={p.id} data-testid={`admin-payment-${p.id}`}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className={`rounded-md border ${STATUS_COLOR[p.status] || "border-white/20"} bg-black/40 p-3`}
            >
              <div className="flex items-start gap-3">
                <Receipt className={`w-5 h-5 ${STATUS_COLOR[p.status]?.split(" ")[0] || ""} mt-0.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm text-white font-medium">
                      {p.currency} ${Number(p.amount).toLocaleString()} · <span className="text-gold/80">{p.method.replace("_", " ")}</span>
                    </div>
                    <div className={`text-xs ${STATUS_COLOR[p.status]?.split(" ")[0] || ""}`}>{p.status.replace("_", " ")}</div>
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    From: <span className="text-white/80">{p.user_name || p.user_email}</span>
                    {p.user_email && <span className="text-white/40"> ({p.user_email})</span>}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">
                    {p.purpose.replace("_", " ")}{p.purpose_ref_id ? ` · ref ${p.purpose_ref_id.slice(0, 12)}` : ""}
                    {p.note ? ` · ${p.note}` : ""}
                  </div>
                  <div className="text-[10px] text-white/40 mt-1">id {p.id.slice(0, 8)} · {new Date(p.created_at).toLocaleString()}</div>
                  {p.receipt_url && (
                    <a href={p.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-gold/80 hover:text-gold mt-2">
                      <ExternalLink className="w-3 h-3" /> View receipt
                    </a>
                  )}
                  {p.rejection_reason && (
                    <div className="text-xs text-rose-300 mt-2 italic">"{p.rejection_reason}"</div>
                  )}
                  <ApproveRejectPanel intent={p} onChange={load} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
